import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import CartDrawer from '../home/components/CartDrawer';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';

export default function CookiesPage() {
  const [activeSection, setActiveSection] = useState('introduction');
  const [isCartOpen, setIsCartOpen] = useState(false);

  useSEO({
    title: 'Cookies Policy - Kinetic Jewelry | Cookie Usage & Management',
    description: 'Learn about how Kinetic Jewelry uses cookies and similar tracking technologies. Understand cookie types, purposes, and how to manage your cookie preferences for our website.',
    canonical: '/cookies',
    ogType: 'website',
  });

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['introduction', 'what-are-cookies', 'types-of-cookies', 'how-we-use', 'third-party', 'managing-cookies', 'cookie-consent', 'changes', 'contact'];
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const sections = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'what-are-cookies', label: 'What Are Cookies' },
    { id: 'types-of-cookies', label: 'Types of Cookies' },
    { id: 'how-we-use', label: 'How We Use Cookies' },
    { id: 'third-party', label: 'Third-Party Cookies' },
    { id: 'managing-cookies', label: 'Managing Cookies' },
    { id: 'cookie-consent', label: 'Cookie Consent' },
    { id: 'changes', label: 'Changes to Policy' },
    { id: 'contact', label: 'Contact Us' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <Navbar onCartClick={() => setIsCartOpen(true)} />
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* Hero Section */}
      <div className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/60f987aadf00fbd22a217840a3433ef1.jpg"
            alt="Cookies Policy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-6">
          <div className="inline-block px-4 py-1.5 bg-cyan-500/20 border border-cyan-500/30 rounded-full mb-6">
            <span className="text-cyan-400 text-sm font-medium">Legal</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Cookies Policy
          </h1>
          <p className="text-gray-300 text-lg">
            Last Updated: January 2025
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <nav className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6 border border-gray-700/50">
                <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
                  Contents
                </h3>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-all whitespace-nowrap ${
                          activeSection === section.id
                            ? 'bg-cyan-500/20 text-cyan-400 font-medium'
                            : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                        }`}
                      >
                        {section.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 max-w-3xl">
            <div className="prose prose-invert prose-cyan max-w-none">
              
              {/* Introduction */}
              <section id="introduction" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  Introduction
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  <p>
                    This Cookies Policy explains how Kinetic Jewelry ("we", "us", or "our") uses cookies and similar tracking technologies when you visit our website. This policy provides you with clear and comprehensive information about the cookies we use and the purposes for using them.
                  </p>
                  <p>
                    By continuing to browse or use our website, you agree to our use of cookies as described in this policy. If you do not agree with our use of cookies, you should adjust your browser settings accordingly or refrain from using our website.
                  </p>
                </div>
              </section>

              {/* What Are Cookies */}
              <section id="what-are-cookies" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  What Are Cookies
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  <p>
                    Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
                  </p>
                  <p>
                    Cookies allow websites to recognize your device and store some information about your preferences or past actions. They help improve your browsing experience by enabling websites to remember your settings, authenticate your login, and provide personalized content.
                  </p>
                  <p>
                    Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device after you close your browser and are used when you return to the website. Session cookies are temporary and are deleted when you close your browser.
                  </p>
                </div>
              </section>

              {/* Types of Cookies */}
              <section id="types-of-cookies" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  Types of Cookies We Use
                </h2>
                <div className="text-gray-300 space-y-6 leading-relaxed">
                  <p>
                    We use different types of cookies on our website, each serving specific purposes:
                  </p>

                  <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-cyan-400 mb-3">
                      1. Essential Cookies
                    </h3>
                    <p>
                      These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt out of these cookies as they are essential for the website to work.
                    </p>
                    <p className="mt-2 text-sm text-gray-400">
                      Examples: Session management, authentication, security features, shopping cart functionality
                    </p>
                  </div>

                  <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-cyan-400 mb-3">
                      2. Analytics Cookies
                    </h3>
                    <p>
                      These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website's performance and user experience.
                    </p>
                    <p className="mt-2 text-sm text-gray-400">
                      Examples: Google Analytics, page view tracking, user behavior analysis, traffic sources
                    </p>
                  </div>

                  <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-cyan-400 mb-3">
                      3. Marketing Cookies
                    </h3>
                    <p>
                      These cookies are used to track visitors across websites to display relevant and engaging advertisements. They may be set by us or by third-party advertising partners with our permission.
                    </p>
                    <p className="mt-2 text-sm text-gray-400">
                      Examples: Retargeting ads, conversion tracking, social media advertising, personalized marketing
                    </p>
                  </div>

                  <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-cyan-400 mb-3">
                      4. Preference Cookies
                    </h3>
                    <p>
                      These cookies enable the website to remember information that changes the way the website behaves or looks, such as your preferred language, region, or theme settings.
                    </p>
                    <p className="mt-2 text-sm text-gray-400">
                      Examples: Language preferences, currency selection, display settings, accessibility options
                    </p>
                  </div>
                </div>
              </section>

              {/* How We Use Cookies */}
              <section id="how-we-use" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  How We Use Cookies
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  <p>
                    We use cookies for various purposes to enhance your experience on our website:
                  </p>
                  <ul className="list-none space-y-3 ml-0">
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Authentication:</strong> To verify your identity and maintain your logged-in state across pages</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Shopping Cart:</strong> To remember items you've added to your cart and maintain your shopping session</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Preferences:</strong> To remember your settings, language choices, and display preferences</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Analytics:</strong> To understand how you use our website and identify areas for improvement</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Marketing:</strong> To deliver relevant advertisements and measure the effectiveness of our campaigns</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Security:</strong> To protect against fraudulent activity and ensure the security of our website</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Third-Party Cookies */}
              <section id="third-party" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  Third-Party Cookies
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  <p>
                    In addition to our own cookies, we may also use various third-party cookies to report usage statistics, deliver advertisements, and provide enhanced functionality. These third parties may collect information about your online activities over time and across different websites.
                  </p>
                  <p>
                    Third-party services we use may include:
                  </p>
                  <ul className="list-none space-y-3 ml-0">
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Google Analytics:</strong> For website analytics and performance tracking</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Social Media Platforms:</strong> For social sharing features and social media advertising</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Advertising Networks:</strong> For displaying relevant advertisements across the web</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                      <span><strong className="text-white">Payment Processors:</strong> For secure payment processing and fraud prevention</span>
                    </li>
                  </ul>
                  <p>
                    Please note that we do not control these third-party cookies. We recommend reviewing the privacy policies of these third parties for more information about their cookie practices.
                  </p>
                </div>
              </section>

              {/* Managing Cookies */}
              <section id="managing-cookies" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  Managing Your Cookies
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  <p>
                    You have the right to decide whether to accept or reject cookies. Most web browsers automatically accept cookies, but you can usually modify your browser settings to decline cookies if you prefer.
                  </p>
                  <p>
                    Here's how to manage cookies in popular browsers:
                  </p>
                  <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-6 space-y-3">
                    <p><strong className="text-white">Google Chrome:</strong> Settings → Privacy and security → Cookies and other site data</p>
                    <p><strong className="text-white">Mozilla Firefox:</strong> Options → Privacy &amp; Security → Cookies and Site Data</p>
                    <p><strong className="text-white">Safari:</strong> Preferences → Privacy → Manage Website Data</p>
                    <p><strong className="text-white">Microsoft Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</p>
                  </div>
                  <p>
                    Please note that if you choose to block or delete cookies, some features of our website may not function properly, and your user experience may be affected. Essential cookies cannot be disabled as they are necessary for the website to function.
                  </p>
                  <p>
                    You can also opt out of interest-based advertising by visiting the Digital Advertising Alliance's opt-out page at <span className="text-cyan-400">www.aboutads.info/choices</span> or the Network Advertising Initiative's opt-out page at <span className="text-cyan-400">www.networkadvertising.org/choices</span>.
                  </p>
                </div>
              </section>

              {/* Cookie Consent */}
              <section id="cookie-consent" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  Cookie Consent
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  <p>
                    When you first visit our website, you may see a cookie banner informing you about our use of cookies. By clicking "Accept" or continuing to use our website, you consent to our use of cookies as described in this policy.
                  </p>
                  <p>
                    You can withdraw your consent at any time by adjusting your browser settings or using the cookie preference center available on our website. However, please note that withdrawing consent may affect your ability to access certain features and functionality.
                  </p>
                  <p>
                    For essential cookies that are necessary for the website to function, consent is not required as they are exempt under applicable data protection laws.
                  </p>
                </div>
              </section>

              {/* Changes to Policy */}
              <section id="changes" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  Changes to This Policy
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  <p>
                    We may update this Cookies Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on this page with a new "Last Updated" date.
                  </p>
                  <p>
                    We encourage you to review this Cookies Policy periodically to stay informed about how we use cookies and protect your information. Your continued use of our website after any changes indicates your acceptance of the updated policy.
                  </p>
                  <p>
                    If we make significant changes that affect your rights, we may provide additional notice through email or a prominent notice on our website.
                  </p>
                </div>
              </section>

              {/* Contact */}
              <section id="contact" className="mb-12 scroll-mt-24">
                <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="w-1 h-8 bg-cyan-500 rounded-full"></span>
                  Contact Us
                </h2>
                <div className="text-gray-300 space-y-4 leading-relaxed">
                  <p>
                    If you have any questions, concerns, or requests regarding this Cookies Policy or our use of cookies, please don't hesitate to contact us:
                  </p>
                  <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-6 space-y-3">
                    <p><strong className="text-white">Email:</strong> <span className="text-cyan-400">privacy@kineticjewelry.com</span></p>
                    <p><strong className="text-white">Phone:</strong> <span className="text-cyan-400">+1 (555) 123-4567</span></p>
                    <p><strong className="text-white">Address:</strong> 123 Motion Street, Design District, New York, NY 10001</p>
                  </div>
                  <p>
                    We are committed to addressing your concerns and will respond to your inquiry as soon as possible, typically within 48 hours during business days.
                  </p>
                </div>
              </section>

            </div>

            {/* Bottom CTA */}
            <div className="mt-16 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">
                Ready to Explore Our Collection?
              </h3>
              <p className="text-gray-300 mb-6">
                Discover our unique kinetic jewelry pieces that blend art and motion
              </p>
              <Link
                to="/shop"
                className="inline-block px-8 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                Shop Now
              </Link>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}